'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { isPublicPath as isPublicAuthPath } from '@/config/routes';
import { useFeatures } from '@/hooks/useFeatures';
import { getFeatureForRoute } from '@/config/features';

/**
 * Check if the path is a static asset or API route that should bypass auth
 */
const isStaticOrApiPath = (pathname: string): boolean => {
  // Static file extensions that should be publicly accessible
  const staticExtensions = [
    '.svg', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico',
    '.css', '.js', '.json', '.xml', '.txt', '.pdf',
    '.woff', '.woff2', '.ttf', '.eot', '.otf'
  ];
  
  // Check if path has a static file extension
  const hasStaticExtension = staticExtensions.some(ext => pathname.toLowerCase().endsWith(ext));
  
  // Check if path starts with /api (Next.js API routes)
  const isApiRoute = pathname.startsWith('/api');
  
  // Check if path starts with /_next (Next.js internal routes)
  const isNextInternal = pathname.startsWith('/_next');
  
  return hasStaticExtension || isApiRoute || isNextInternal;
};

/**
 * RouteGuard Component
 * 
 * Protects routes by checking authentication status and validating tokens.
 * Redirects unauthenticated users to /login (except for /login itself and public assets).
 * 
 * Public paths (bypass authentication):
 * - /login page
 * - Static assets (.svg, .png, .jpg, etc.)
 * - API routes (/api/*)
 * - Next.js internal routes (/_next/*)
 */
export function RouteGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, isLoading, validateToken } = useAuth();
  const { hasFeature, getFirstAvailableRoute } = useFeatures();
  const [isValidating, setIsValidating] = useState(false);

  // Validate token on mount and when pathname changes
  useEffect(() => {
    const checkAuth = async () => {
      // Skip validation for public paths and public auth pages
      if (isPublicAuthPath(pathname) || isStaticOrApiPath(pathname)) {
        return;
      }

      // If already authenticated, validate token
      if (isAuthenticated && !isLoading) {
        setIsValidating(true);
        try {
          const isValid = await validateToken();
          if (!isValid) {
            // Token is invalid, redirect to login
            router.push('/login');
          }
        } catch (error) {
          console.error('Token validation error:', error);
          router.push('/login');
        } finally {
          setIsValidating(false);
        }
      }
    };

    checkAuth();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Don't redirect if we're still loading auth state or validating
    if (isLoading || isValidating) {
      return;
    }

    // Allow access to public auth pages (from routes.ts)
    if (isPublicAuthPath(pathname)) {
      // If already authenticated and on login, redirect to first available feature
      if (isAuthenticated && pathname === '/login') {
        const firstRoute = getFirstAvailableRoute();
        router.push(firstRoute);
      }
      return;
    }

    // Allow access to public paths (static assets, API routes, etc.)
    if (isStaticOrApiPath(pathname)) {
      return;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }

    // Check feature access for protected routes
    // Only check if user is authenticated
    if (isAuthenticated) {
      const featureId = getFeatureForRoute(pathname);
      
      // If route maps to a feature, check if user has access
      if (featureId && !hasFeature(featureId)) {
        // User doesn't have access to this feature, redirect to first available
        const firstRoute = getFirstAvailableRoute();
        router.push(firstRoute);
      }
    }
  }, [isAuthenticated, isLoading, isValidating, pathname, router, hasFeature, getFirstAvailableRoute]);

  // Show nothing while checking auth - no loading spinner to avoid flash
  if (isLoading || isValidating) {
    return <>{children}</>;
  }

  // Allow public paths to render without authentication
  if (isStaticOrApiPath(pathname)) {
    return <>{children}</>;
  }

  // Don't render protected content if not authenticated (except public auth pages)
  if (!isAuthenticated && !isPublicAuthPath(pathname)) {
    return null;
  }

  return <>{children}</>;
}
